/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Abstract input action enum — platform-agnostic representation of all
 * possible user inputs from D-pad, remote, keyboard, and pointer devices.
 */
export enum InputAction {
  // ─── Navigation ───
  NAV_UP = "NAV_UP",
  NAV_DOWN = "NAV_DOWN",
  NAV_LEFT = "NAV_LEFT",
  NAV_RIGHT = "NAV_RIGHT",
  NAV_OK = "NAV_OK",

  // ─── System ───
  BACK = "BACK",
  EXIT = "EXIT",

  // ─── Custom Actions ───
  INFO = "INFO",
  GUIDE = "GUIDE",
  MENU = "MENU",

  // ─── Color Buttons (TV remotes) ───
  COLOR_RED = "COLOR_RED",
  COLOR_GREEN = "COLOR_GREEN",
  COLOR_YELLOW = "COLOR_YELLOW",
  COLOR_BLUE = "COLOR_BLUE",

  // ─── Media ───
  PLAY_PAUSE = "PLAY_PAUSE",
  STOP = "STOP",
  REWIND = "REWIND",
  FAST_FORWARD = "FAST_FORWARD",

  // ─── Channel ───
  CHANNEL_UP = "CHANNEL_UP",
  CHANNEL_DOWN = "CHANNEL_DOWN",

  // ─── Pointer / Mouse ───
  POINTER_CLICK = "POINTER_CLICK",
  POINTER_CONTEXT_MENU = "POINTER_CONTEXT_MENU",
  POINTER_HOVER = "POINTER_HOVER",
  POINTER_LEAVE = "POINTER_LEAVE",
  SCROLL_UP = "SCROLL_UP",
  SCROLL_DOWN = "SCROLL_DOWN",

  // ─── Number Keys (0–9) ───
  NUM_0 = "NUM_0",
  NUM_1 = "NUM_1",
  NUM_2 = "NUM_2",
  NUM_3 = "NUM_3",
  NUM_4 = "NUM_4",
  NUM_5 = "NUM_5",
  NUM_6 = "NUM_6",
  NUM_7 = "NUM_7",
  NUM_8 = "NUM_8",
  NUM_9 = "NUM_9",

  // ─── Misc ───
  UNKNOWN = "UNKNOWN"
}

/** Navigation actions — D-pad / arrow keys */
export const NAVIGATION_ACTIONS: ReadonlySet<InputAction> = new Set([
  InputAction.NAV_UP,
  InputAction.NAV_DOWN,
  InputAction.NAV_LEFT,
  InputAction.NAV_RIGHT,
  InputAction.NAV_OK
]);

/** Pointer/mouse actions */
export const POINTER_ACTIONS: ReadonlySet<InputAction> = new Set([
  InputAction.POINTER_CLICK,
  InputAction.POINTER_CONTEXT_MENU,
  InputAction.POINTER_HOVER,
  InputAction.POINTER_LEAVE,
  InputAction.SCROLL_UP,
  InputAction.SCROLL_DOWN
]);

/** Number key actions (0–9) */
export const NUMBER_ACTIONS: ReadonlySet<InputAction> = new Set([
  InputAction.NUM_0,
  InputAction.NUM_1,
  InputAction.NUM_2,
  InputAction.NUM_3,
  InputAction.NUM_4,
  InputAction.NUM_5,
  InputAction.NUM_6,
  InputAction.NUM_7,
  InputAction.NUM_8,
  InputAction.NUM_9
]);

export function isNavigationAction(action: InputAction): boolean {
  return NAVIGATION_ACTIONS.has(action);
}

export function isPointerAction(action: InputAction): boolean {
  return POINTER_ACTIONS.has(action);
}

export function isNumberAction(action: InputAction): boolean {
  return NUMBER_ACTIONS.has(action);
}

/** Direction of D-pad / arrow-key navigation */
export type NavigationDirection = "up" | "down" | "left" | "right";

/** Map InputAction → NavigationDirection (only for directional nav actions) */
export function actionToDirection(
  action: InputAction
): NavigationDirection | null {
  switch (action) {
    case InputAction.NAV_UP:
      return "up";
    case InputAction.NAV_DOWN:
      return "down";
    case InputAction.NAV_LEFT:
      return "left";
    case InputAction.NAV_RIGHT:
      return "right";
    default:
      return null;
  }
}
